import { Button } from "~/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/atoms/dialog";
import { useDocument } from "~/api/v2/useDocument";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check } from "lucide-react";
import { CenterSpinner } from "~/atoms/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import { Form } from "~/atoms/form";
import { DocumentScheduledReviewFields } from "~/molecules/document-fields/DocumentScheduledReviewFields";
import NoResult from "~/molecules/NoResult";
import z from "zod";
import i18n from "~/i18n";
import { type FetchError, type FileType } from "~/api/types";
import { downloadDocument } from "~/api/v2/requests";
import {
  analyzeTrackChanges,
  type TrackChangeStatus,
} from "~/helpers/docx-track-changes";
import { Alert, AlertDescription, AlertTitle } from "~/atoms/alert";

type PublishModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (params: {
    scheduleReviewFrom?: Date | null;
    scheduleReviewTo?: Date | null;
    scheduleReviewPeriod?: string | null;
  }) => void;
  documentId: number;
  loading?: boolean;
  disabled?: boolean;
  buttonText: string;
  canManage: boolean;
  versionFileType: FileType;
  versionId: number;
  error?: FetchError | null;
};

const formId = "ready-to-publish-form";

const todayAtMidnight = new Date().setHours(0, 0, 0, 0);

const scheduledReviewForm = z
  .object({
    scheduleReviewPeriod: z.string().nullable(),
    scheduleReviewFrom: z.date().nullable(),
    scheduleReviewTo: z.date().nullable(),
  })
  .refine(
    (data) =>
      data.scheduleReviewFrom
        ? new Date(data.scheduleReviewFrom).setHours(0, 0, 0, 0) >=
          todayAtMidnight
        : true,
    {
      message: i18n.t("validations.startDateMustNotBeInPast"),
      path: ["scheduleReviewFrom"],
    },
  )
  .refine(
    (data) =>
      data.scheduleReviewTo
        ? new Date(data.scheduleReviewTo).setHours(0, 0, 0, 0) >=
          todayAtMidnight
        : true,
    {
      message: i18n.t("validations.endDateMustNotBeInPast"),
      path: ["scheduleReviewTo"],
    },
  )
  .refine(
    (data) =>
      data.scheduleReviewPeriod || data.scheduleReviewTo
        ? data.scheduleReviewFrom !== null
        : true,
    {
      message: i18n.t("validations.startDateRequired"),
      path: ["scheduleReviewFrom"],
    },
  )
  .refine(
    (data) =>
      data.scheduleReviewFrom && data.scheduleReviewTo
        ? data.scheduleReviewTo > data.scheduleReviewFrom
        : true,
    {
      message: i18n.t("validations.endDateAfter"),
      path: ["scheduleReviewTo"],
    },
  );

export function PublishModal({
  open,
  onOpenChange,
  onConfirm,
  documentId,
  loading,
  disabled,
  buttonText,
  canManage,
  versionFileType,
  versionId,
  error,
}: PublishModalProps) {
  const { t } = useTranslation();
  const { data: document, isLoading } = useDocument(documentId);
  const [trackChangeStatus, setTrackChangeStatus] =
    useState<TrackChangeStatus | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;

    const fetchTrackChangesStatus = async () => {
      if (versionFileType !== "docx") {
        return;
      }

      const blob = await downloadDocument(versionId).catch(() => null);
      if (!blob) {
        return;
      }

      const status = await analyzeTrackChanges(blob).catch(() => null);
      if (!status || cancelled) {
        return;
      }

      setTrackChangeStatus(status);
    };

    void fetchTrackChangesStatus();

    return () => {
      cancelled = true;
      setTrackChangeStatus(null);
    };
  }, [open, versionFileType, versionId]);

  const form = useForm({
    resolver: zodResolver(scheduledReviewForm),
    values: {
      scheduleReviewPeriod: document?.scheduleReviewPeriod ?? null,
      scheduleReviewFrom: document?.scheduleReviewFrom
        ? new Date(document.scheduleReviewFrom)
        : null,
      scheduleReviewTo: document?.scheduleReviewTo
        ? new Date(document.scheduleReviewTo)
        : null,
    },
  });

  const [scheduleReviewFrom, scheduleReviewTo] = useWatch({
    control: form.control,
    name: ["scheduleReviewFrom", "scheduleReviewTo"],
  });

  if (isLoading) {
    return <CenterSpinner />;
  }

  if (!document) {
    return <NoResult />;
  }

  const showMissingReviewDatesWarning =
    !scheduleReviewFrom && !scheduleReviewTo;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="min-w-48" disabled={disabled}>
          <Check />
          {buttonText}
        </Button>
      </DialogTrigger>
      <DialogContent className="gap-0 p-0">
        <DialogHeader className="p-6">
          <DialogTitle>{t("commons.workflow.readyToPublish")}</DialogTitle>
          <DialogDescription>
            {t("commons.workflow.publishDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-6 px-6 pb-6">
          <Form {...form}>
            <form id={formId} onSubmit={form.handleSubmit(onConfirm)}>
              <DocumentScheduledReviewFields />
            </form>
          </Form>

          {error?.code === "DOCUMENT_OPEN_IN_EDITOR" && (
            <Alert variant="destructive">
              <AlertTriangle />
              <AlertTitle>
                {t("commons.workflow.documentOpenInEditorError")}
              </AlertTitle>
              <AlertDescription>
                {t("commons.workflow.documentOpenInEditorErrorDescription")}
              </AlertDescription>
            </Alert>
          )}

          {trackChangeStatus?.hasTrackRevisionsSetting && (
            <Alert variant="warn">
              <AlertTriangle />
              <AlertTitle>
                {t("commons.workflow.trackChangesEnabledWarning")}
              </AlertTitle>
              <AlertDescription>
                {t("commons.workflow.trackChangesEnabledWarningDescription")}
              </AlertDescription>
            </Alert>
          )}

          {trackChangeStatus?.hasRevisionNodes && (
            <Alert variant="warn">
              <AlertTriangle />
              <AlertTitle>
                {t("commons.workflow.unresolvedSuggestionsWarning")}
              </AlertTitle>
              <AlertDescription>
                {t("commons.workflow.unresolvedSuggestionsWarningDescription")}
              </AlertDescription>
            </Alert>
          )}

          {trackChangeStatus?.hasCommentNodes && (
            <Alert variant="warn">
              <AlertTriangle />
              <AlertTitle>{t("commons.workflow.commentsWarning")}</AlertTitle>
              <AlertDescription>
                {t("commons.workflow.commentsWarningDescription")}
              </AlertDescription>
            </Alert>
          )}

          {showMissingReviewDatesWarning && (
            <Alert variant="warn">
              <AlertTriangle />
              <AlertTitle>
                {canManage
                  ? t("commons.workflow.missingReviewDates")
                  : t("commons.workflow.noReviewSchedule")}
              </AlertTitle>
              <AlertDescription>
                {canManage
                  ? t("commons.workflow.missingReviewDatesDescription")
                  : t("commons.workflow.noReviewScheduleDescription")}
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter className="flex flex-row justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              {t("commons.workflow.cancel")}
            </Button>
            <Button type="submit" form={formId} disabled={loading}>
              {t("commons.workflow.publishDocument")}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
